import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    createBillingLink,
    deleteCustomer as deleteCustomerRequest,
    manageMembership as manageMembershipRequest,
    sendInvoice as sendInvoiceRequest,
    updateCustomer as updateCustomerRequest,
} from '@/lib/api/audience';
import type { InvoicePayload, ManageMembershipPayload, UpdateCustomerPayload } from '../types/customer';
import { QUERY_KEYS } from '@/lib/constants';
import { copyToClipboard } from '@/lib/helpers';

export interface UseCustomerActionsResult {
    updateCustomerMutation: ReturnType<typeof useMutation<void, Error, UpdateCustomerPayload>>;
    deleteCustomerMutation: ReturnType<typeof useMutation<void, Error, void>>;
    manageMembershipMutation: ReturnType<typeof useMutation<void, Error, ManageMembershipPayload>>;
    copyBillingLinkMutation: ReturnType<typeof useMutation<string, Error, string>>;
    sendInvoiceMutation: ReturnType<typeof useMutation<void, Error, InvoicePayload>>;
}

export function useCustomerActions(userFanId: number): UseCustomerActionsResult {
    const queryClient = useQueryClient();

    // Update Customer Mutation
    const updateCustomerMutation = useMutation({
        mutationFn: (payload: UpdateCustomerPayload) => updateCustomerRequest(userFanId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_DETAILS, userFanId] });
        },
    });

    // Delete Customer Mutation
    const deleteCustomerMutation = useMutation({
        mutationFn: () => deleteCustomerRequest(userFanId),
        onSuccess: () => {
            queryClient.removeQueries({ queryKey: [QUERY_KEYS.CUSTOMER_DETAILS, userFanId] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUDIENCE] });
        },
    });

    // Manage Membership Mutation
    const manageMembershipMutation = useMutation({
        mutationFn: (payload: ManageMembershipPayload) => manageMembershipRequest(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CUSTOMER_DETAILS, userFanId] });
        },
    });

    // Copy Billing Link Mutation
    const copyBillingLinkMutation = useMutation({
        mutationFn: async (subscriptionId: string) => {
            const link = await createBillingLink(subscriptionId);

            await copyToClipboard(link, {
                successMessage: 'Billing link copied!',
                errorMessage: 'Failed to copy billing link to clipboard',
            });
            return link;
        },
    });

    // Send Invoice Mutation
    const sendInvoiceMutation = useMutation({
        mutationFn: (payload: InvoicePayload) => sendInvoiceRequest(userFanId, payload),
    });

    return {
        updateCustomerMutation,
        deleteCustomerMutation,
        manageMembershipMutation,
        copyBillingLinkMutation,
        sendInvoiceMutation,
    };
}
